import { Outlet, useLocation } from '@remix-run/react'
import { SquarePen, Menu, Cpu, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'
import { LocaleLink } from '#app/components/locale-link.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { SET_IS_MENU_VISIBLE } from '#app/constants/chat.ts'
import { ClientOnly } from '#app/hooks/use-client-only.tsx'
import { useCurrentLanguage } from '#app/hooks/use-current-language.tsx'
import { useGlobalChatStore } from '#app/routes/chat+/index.tsx'
import Conversation from './chat/conversation/index.tsx'
import IntakeModal from './chat/intake-modal/index.tsx'
import PaymentModal from './chat/payment-modal/index.tsx'

const Chat = () => {
	const { t } = useTranslation('chat')
	const location = useLocation()
	const currentLanguage = useCurrentLanguage()
	const dispatch = useGlobalChatStore(useShallow(state => state.dispatch))
	const menuIsVisible = useGlobalChatStore(state => state.menuIsVisible)
	const coinsLeft = useGlobalChatStore(state => state.coinsLeft)
	const intakeStatus = useGlobalChatStore(state => state.intakeStatus)

	const [showIntakeButton, setShowIntakeButton] = useState(false)

	useEffect(() => {
		if (
			intakeStatus === 'incomplete-all' ||
			intakeStatus === 'incomplete-questions' ||
			intakeStatus === 'incomplete-tests'
		) {
			setShowIntakeButton(true)
		}
	}, [intakeStatus])

	const isChatPage = location.pathname === `/${currentLanguage}/chat`

	return (
		<div className="flex h-full max-w-full flex-1 flex-col">
			<div className="sticky top-0 z-10 flex items-center border-b border-white/20  pl-1 pt-1 text-primary-400 sm:pl-3 lg:hidden">
				<button
					type="button"
					className="-ml-0.5 -mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-md hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white dark:hover:text-white"
					onClick={() =>
						dispatch({ type: SET_IS_MENU_VISIBLE, isVisible: !menuIsVisible })
					}
				>
					<span className="sr-only">{t('intake.clickHereForIntakeTitle')}</span>
					<Menu />
				</button>
				<div className="flex flex-1 justify-center">
					{!menuIsVisible && (
						<img
							alt="Wiser logo"
							src="/img/wiser-logo-small.png"
							className="h-8"
						/>
					)}
				</div>
				<div className="flex flex-row">
					<ClientOnly>
						{() => (
							<PaymentModal
								customButton={
									<div className="-ml-10 mr-1 flex cursor-pointer flex-row">
										<Cpu />: {coinsLeft}
									</div>
								}
							/>
						)}
					</ClientOnly>

					<LocaleLink
						className="px-3"
						lang={currentLanguage}
						route="chat"
						onClick={() => {
							dispatch({
								type: 'DELETE_MESSAGE',
							})
							dispatch({ type: SET_IS_MENU_VISIBLE, isVisible: false })
						}}
					>
						<SquarePen />
					</LocaleLink>
				</div>
			</div>
			{showIntakeButton && (
				<div className="flex w-full flex-col justify-between gap-2 bg-background px-4 py-3 shadow-xl lg:hidden">
					<div className="flex w-full flex-row justify-between">
						<p className="mr-2 text-sm font-semibold">
							{t('intake.clickHereForIntakeTitle')}
						</p>
						<X
							onClick={() => setShowIntakeButton(false)}
							className="cursor-pointer"
						/>
					</div>
					<ClientOnly>
						{() => (
							<IntakeModal
								customButton={
									<Button>{t('intake.clickHereForIntakeButton')}</Button>
								}
							/>
						)}
					</ClientOnly>
				</div>
			)}
			{isChatPage ? <Conversation /> : <Outlet />}
		</div>
	)
}

export default Chat
